import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Rectangle, useMapEvents, Tooltip } from 'react-leaflet';
import { 
  ArrowLeft, 
  Map,
  X,
  CheckCircle,
  AlertCircle,
  Navigation,
  Sparkles,
  Copy,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import { geocodingService } from '../services/geocoding';
import { getDocuments, updateDocument } from '../services/firebase';
import { logAddressComponentsSample } from '../services/addressSampling';
import { placeIdLookup } from '../utils/placeIdLookup';

interface AddressValidatorProps {
  onBack: () => void;
}

type GridCell = { id: number; bbox: [number, number, number, number]; masked: boolean };

const MumbaiCenter: [number, number] = [19.0760, 72.8777];

const OsmGridPanel: React.FC = () => {
  // Defaults aligned with BRD
  const [delta, setDelta] = useState(0.004);
  const [tileZoom, setTileZoom] = useState(14);
  const [threshGreen, setThreshGreen] = useState(0.36);
  const [threshBlue, setThreshBlue] = useState(0.36);
  const [alpha, setAlpha] = useState(0.35);
  const [cells, setCells] = useState<GridCell[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [samples, setSamples] = useState<any[]>([]);
  // Map of localityName -> total outlets in that locality (from 'outlets' collection)
  const [outletCountsByLocality, setOutletCountsByLocality] = useState<Record<string, number>>({});
  const [coverage, setCoverage] = useState<Record<number, { area?: string; locality?: string; sampleCount: number; conflict?: boolean; sampleCountsByLocality?: Record<string, number>; outletCountsByLocality?: Record<string, number>; outletTotal?: number }>>({});
  const [extracting, setExtracting] = useState(false);
  const [progress, setProgress] = useState<{ total: number; done: number; errors: number }>({ total: 0, done: 0, errors: 0 });
  const cancelExtractRef = useRef<{ cancel: boolean }>({ cancel: false });
  const [autoClassify, setAutoClassify] = useState(true);

  // Map bounds state
  const [bounds, setBounds] = useState<{ minLon: number; minLat: number; maxLon: number; maxLat: number } | null>(null);
  const [leafletZoom, setLeafletZoom] = useState<number>(12);
  const MAX_RENDERED_CELLS = 1500; // viability guard to avoid rendering too many overlays

  const approxCells = useMemo(() => {
    if (!bounds) return 0;
    const dx = Math.max(0, bounds.maxLon - bounds.minLon);
    const dy = Math.max(0, bounds.maxLat - bounds.minLat);
    if (delta <= 0) return 0;
    const nx = Math.ceil(dx / delta);
    const ny = Math.ceil(dy / delta);
    return nx * ny;
  }, [bounds, delta]);

  // Load samples on mount
  useEffect(() => {
    (async () => {
      try {
        const docs = await getDocuments('address_samples');
        setSamples(docs);
      } catch (e) {
        console.warn('Failed to load address_samples for OSM grid:', e);
      }
    })();
  }, []);

  // Load outlets and aggregate counts by locality
  useEffect(() => {
    (async () => {
      try {
        const outs = await getDocuments('outlets');
        const counts: Record<string, number> = {};
        for (const o of outs) {
          const name: string | undefined = o.localityName || o.locality || o.parserOutput?.suggestedLocalityName;
          if (!name) continue;
          counts[name] = (counts[name] || 0) + 1;
        }
        setOutletCountsByLocality(counts);
      } catch (e) {
        console.warn('Failed to load outlets for locality counts:', e);
      }
    })();
  }, []);

  // Derive coverage from samples + cells; show outlet counts by locality in tooltip
  useEffect(() => {
    if (!cells.length || !samples.length) {
      // Even if samples are empty, we can still show outlet totals for a single known locality later
      if (!cells.length) { setCoverage({}); return; }
    }
    const cov: Record<number, { area?: string; locality?: string; sampleCount: number; conflict?: boolean; sampleCountsByLocality?: Record<string, number>; outletCountsByLocality?: Record<string, number>; outletTotal?: number }> = {};
    for (const cell of cells) {
      const [minLon, minLat, maxLon, maxLat] = cell.bbox;
      let sampleCount = 0;
      let area: string | undefined;
      const localities = new Set<string>();
      const sampleCountsByLocality: Record<string, number> = {};
      for (const s of samples) {
        const lat = s.coordinates?.lat;
        const lon = s.coordinates?.lng;
        if (lat == null || lon == null) continue;
        if (lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat) {
          sampleCount++;
          const parsedArea = s.parserOutput?.suggestedAreaName;
          const parsedLocality = s.parserOutput?.suggestedLocalityName;
          if (!area && parsedArea) area = parsedArea;
          if (parsedLocality) {
            localities.add(parsedLocality);
            sampleCountsByLocality[parsedLocality] = (sampleCountsByLocality[parsedLocality] || 0) + 1;
          }
        }
      }
      if (sampleCount > 0 || area || localities.size) {
        const locality = localities.size === 1 ? Array.from(localities)[0] : undefined;
        const conflict = localities.size > 1;
        // Compute outlet totals for the localities present in this cell using pre-aggregated counts
        const outletsCounts: Record<string, number> = {};
        let outletTotal = 0;
        Array.from(localities).forEach((name) => {
          const c = outletCountsByLocality[name] || 0;
          outletsCounts[name] = c;
          outletTotal += c;
        });
        cov[cell.id] = {
          area,
          locality,
          sampleCount,
          conflict,
          sampleCountsByLocality: Object.keys(sampleCountsByLocality).length ? sampleCountsByLocality : undefined,
          outletCountsByLocality: Object.keys(outletsCounts).length ? outletsCounts : undefined,
          outletTotal: outletTotal || undefined,
        };
      }
    }
    setCoverage(cov);
  }, [cells, samples, outletCountsByLocality]);

  // Helper to hash a string to HSL color for locality
  const colorForLocality = (name: string): string => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    const hue = h % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  const classify = async () => {
    if (!bounds) return;
    // Viability guard: skip classify if area would create too many cells
    if (approxCells > MAX_RENDERED_CELLS) {
      setError(`Selected area too large (~${approxCells} cells). Zoom in or increase grid size (delta).`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('http://localhost:5001/grid/analyze', {
        bbox: [bounds.minLon, bounds.minLat, bounds.maxLon, bounds.maxLat],
        delta,
        zoom: tileZoom || leafletZoom,
        thresh_green: threshGreen,
        thresh_blue: threshBlue,
        alpha,
        return_image: false,
      }, { timeout: 30000 });
      if (res.data?.success && Array.isArray(res.data.cells)) {
        setCells(res.data.cells as GridCell[]);
      } else {
        setError(res.data?.error || 'Classification failed');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to call grid analyzer');
    } finally {
      setLoading(false);
    }
  };

  // Auto-classify when inputs change (debounced)
  useEffect(() => {
    if (!autoClassify) return;
    const t = setTimeout(() => {
      classify();
    }, 500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds, delta, tileZoom, threshGreen, threshBlue, alpha, autoClassify]);

  const extract = async () => {
    if (!cells.length) return;
    setExtracting(true);
    cancelExtractRef.current.cancel = false;
    try {
      // Candidates: non-red and without locality coverage
      const candidates = cells.filter(c => !c.masked && !coverage[c.id]?.locality);
      setProgress({ total: candidates.length, done: 0, errors: 0 });
      let done = 0;
      let errors = 0;
      for (const c of candidates) {
        if (cancelExtractRef.current.cancel) break;
        const [minLon, minLat, maxLon, maxLat] = c.bbox;
        const lat = (minLat + maxLat) / 2;
        const lon = (minLon + maxLon) / 2;
        try {
          const result = await geocodingService.parseForOutletFields(lat, lon);
          if (result.success && result.addressFields) {
            try {
              await logAddressComponentsSample('OSM Grid Extractor', lat, lon, result.rawResponse, result.addressFields);
            } catch (e) {
              // logging failure shouldn't stop extraction
              console.warn('Sample log failed for extracted point:', e);
            }
          } else {
            errors++;
          }
        } catch (ex) {
          errors++;
        }
        done++;
        setProgress({ total: candidates.length, done, errors });
        // throttle to avoid rate limits
        await new Promise(r => setTimeout(r, 350));
      }
      // Refresh samples & coverage
      try {
        const docs = await getDocuments('address_samples');
        setSamples(docs);
      } catch {}
    } finally {
      setExtracting(false);
    }
  };

  const cancelExtract = () => {
    cancelExtractRef.current.cancel = true;
  };

  const BBoxListener: React.FC = () => {
    const map = useMapEvents({
      moveend: () => {
        const b = map.getBounds();
        const z = map.getZoom();
        setLeafletZoom(z);
        setBounds({
          minLon: b.getWest(),
          minLat: b.getSouth(),
          maxLon: b.getEast(),
          maxLat: b.getNorth(),
        });
      },
      zoomend: () => {
        const b = map.getBounds();
        const z = map.getZoom();
        setLeafletZoom(z);
        setBounds({
          minLon: b.getWest(),
          minLat: b.getSouth(),
          maxLon: b.getEast(),
          maxLat: b.getNorth(),
        });
      }
    });
    return null;
  };

  // Initialize bounds/zoom once after map mounts
  const MountInit: React.FC = () => {
    const map = useMapEvents({
      load: () => {
        const b = map.getBounds();
        const z = map.getZoom();
        setLeafletZoom(z);
        setBounds({
          minLon: b.getWest(),
          minLat: b.getSouth(),
          maxLon: b.getEast(),
          maxLat: b.getNorth(),
        });
      }
    });
    return null;
  };

  // Initial bounds estimation on mount if not set after first render
  const initialCenter = useMemo(() => MumbaiCenter, []);
  const candidateCount = useMemo(() => {
    return cells.reduce((acc, c) => acc + ((!c.masked && !coverage[c.id]?.locality) ? 1 : 0), 0);
  }, [cells, coverage]);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Grid size (delta, degrees)</label>
          <input type="number" step="0.001" value={delta} onChange={(e) => setDelta(parseFloat(e.target.value) || 0.001)} className="w-full border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tile zoom</label>
          <input type="number" min={1} max={19} value={tileZoom} onChange={(e) => setTileZoom(parseInt(e.target.value || '13', 10))} className="w-full border rounded px-2 py-1" />
          <div className="text-xs text-gray-500">Leaflet zoom: {leafletZoom}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Green threshold</label>
          <input type="number" step="0.01" min={0} max={1} value={threshGreen} onChange={(e) => setThreshGreen(parseFloat(e.target.value) || 0)} className="w-full border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Blue threshold</label>
          <input type="number" step="0.01" min={0} max={1} value={threshBlue} onChange={(e) => setThreshBlue(parseFloat(e.target.value) || 0)} className="w-full border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Overlay alpha</label>
          <input type="number" step="0.05" min={0} max={1} value={alpha} onChange={(e) => setAlpha(parseFloat(e.target.value) || 0)} className="w-full border rounded px-2 py-1" />
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <label className="inline-flex items-center gap-2 ml-2 text-sm text-gray-700">
          <input type="checkbox" checked={autoClassify} onChange={(e) => setAutoClassify(e.target.checked)} />
          Auto-refresh grid & classification
        </label>
        {approxCells > MAX_RENDERED_CELLS && (
          <span className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded ml-2">
            Too many cells (~{approxCells}). Zoom in or increase delta.
          </span>
        )}
        {error && <span className="text-sm text-red-600 ml-2">{error}</span>}
      </div>

      {/* Small stats */}
      <div className="text-xs text-gray-600 mb-1">
        Cells: {cells.length} · Masked: {cells.filter(c=>c.masked).length} · Conflicts: {Object.values(coverage).filter(x=>x.conflict).length}
      </div>
      {/* Runtime status to help visibility/viability checks */}
      <div className="text-[11px] text-gray-500 mb-2">
        Status: {loading ? 'classifying' : 'idle'} · Candidates: {candidateCount} · Viable: {approxCells <= MAX_RENDERED_CELLS ? 'yes' : 'no'} (approx {approxCells})
      </div>
      {/* Secondary Extract button removed per request */}

      <div className="relative w-full" style={{ height: '560px', minHeight: '24rem' }}>
        {/* Loading bar for grid classification */}
        {loading && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2" style={{ zIndex: 3000 }}>
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-3 py-1 rounded shadow-sm">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <div className="text-xs font-medium">Grid being calculated…</div>
            </div>
          </div>
        )}
        {/* Floating Extract Details button (visible only when grid is visible and not loading) */}
        {cells.length > 0 && cells.length <= MAX_RENDERED_CELLS && (
          <div className="absolute top-2 right-2" style={{ zIndex: 3000 }}>
            <button
              onClick={extract}
              disabled={extracting || loading || approxCells > MAX_RENDERED_CELLS || candidateCount === 0}
              className="px-3 py-2 rounded-md text-white bg-green-600 hover:bg-green-700 border border-green-700 disabled:opacity-50 shadow focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
              title={candidateCount ? `${candidateCount} candidate cell${candidateCount===1?'':'s'}` : 'No candidate cells'}
            >
              {extracting ? 'Extracting…' : 'Extract Details'}
            </button>
            {extracting && (
              <button onClick={cancelExtract} className="ml-2 px-3 py-2 rounded-md text-white bg-red-600 hover:bg-red-700 border border-red-700 shadow focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1">Cancel</button>
            )}
          </div>
        )}
        {/* Rendering skipped notice when too many cells */}
        {cells.length > MAX_RENDERED_CELLS && (
          <div className="absolute top-2 left-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded shadow" style={{ zIndex: 3000 }}>
            Rendering skipped: {cells.length} cells. Zoom in or increase delta.
          </div>
        )}
        <MapContainer
          center={initialCenter}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MountInit />
          <BBoxListener />
          {/* Grid rectangles */}
          {cells.length > MAX_RENDERED_CELLS ? null : cells.map((c) => {
            const [minLon, minLat, maxLon, maxLat] = c.bbox;
            const cov = coverage[c.id];
            const hasLocality = !!cov?.locality;
            const isConflict = !!cov?.conflict;
            const fillColor = isConflict ? '#000000' : (hasLocality ? colorForLocality(cov!.locality!) : 'rgba(0,0,0,0)');
            const opacity = isConflict ? 0.5 : (hasLocality ? 0.22 : 0.0);
            const redMask = c.masked;
            return (
              <Rectangle
                key={c.id}
                bounds={[[minLat, minLon], [maxLat, maxLon]]}
                pathOptions={{
                  color: redMask ? 'rgba(255,0,0,0.6)' : isConflict ? '#000000' : (hasLocality ? fillColor : '#3b82f6'),
                  weight: redMask ? 1 : 0.5,
                  fill: true,
                  fillColor: redMask ? 'rgba(255,0,0,0.35)' : fillColor,
                  fillOpacity: redMask ? alpha : opacity,
                }}
              >
                <Tooltip sticky>
                  <div className="text-xs">
                    {!isConflict ? (
                      <>
                        <div><strong>Locality:</strong> {cov?.locality || '-'}</div>
                        <div><strong>Outlets:</strong> {cov?.locality ? (cov?.outletCountsByLocality?.[cov.locality] ?? 0) : 0}</div>
                      </>
                    ) : (
                      <>
                        <div className="font-semibold">Localities</div>
                        <div className="mt-1 space-y-0.5">
                          {Object.entries(cov?.outletCountsByLocality || {}).map(([name, n]) => (
                            <div key={name}>{name}: {n}</div>
                          ))}
                        </div>
                        <div className="mt-1"><strong>Total outlets:</strong> {cov?.outletTotal ?? 0}</div>
                      </>
                    )}
                  </div>
                </Tooltip>
              </Rectangle>
            );
          })}
        </MapContainer>
      </div>

      {/* Progress modal */}
      {extracting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 shadow-md w-full max-w-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <div className="font-medium text-gray-800">Extracting localities…</div>
            </div>
            <div className="text-sm text-gray-600">This runs Google geocoding sequentially with throttling to avoid rate limits.</div>
            <div className="mt-2 text-xs text-gray-700">{progress.done} / {progress.total} done · {progress.errors} errors</div>
            <div className="mt-2 w-full bg-gray-100 h-2 rounded">
              <div className="bg-blue-600 h-2 rounded" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} />
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={cancelExtract} className="px-3 py-2 rounded-md text-red-600 bg-red-50 border border-red-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AddressValidator: React.FC<AddressValidatorProps> = ({ onBack }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState<'google' | 'osm' | 'reports'>('google');
  const [selectedLocation, setSelectedLocation] = useState({ lat: 0, lng: 0 });
  const [addressResult, setAddressResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rawGoogleResponse, setRawGoogleResponse] = useState<any>(null);

  // Report state
  // Tabs: reports now lives under the Reports tab; keep a local loading state
  const [samples, setSamples] = useState<any[]>([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [deterministicChoice, setDeterministicChoice] = useState<{
    name: string;
    placeId: string;
    type: 'sublocality_level_2' | 'sublocality_level_3' | 'neighborhood' | 'route';
  } | null>(null);
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  useEffect(() => {
    // Initialize map on Mumbai coordinates by default
    initializeMap(19.0760, 72.8777);
  }, []);

  // Load samples when switching to the Reports tab
  useEffect(() => {
    if (activeTab === 'reports') {
      loadSamples();
    }
  }, [activeTab]);

  const loadSamples = async () => {
    try {
      setLoadingReport(true);
      const docs = await getDocuments('address_samples');
      // Sort by createdAt desc if present
      const sorted = docs.sort((a: any, b: any) => {
        const at = (a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime?.() ?? 0);
        const bt = (b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime?.() ?? 0);
        return bt - at;
      });
      setSamples(sorted);
    } catch (e) {
      console.error('Failed to load samples:', e);
    } finally {
      setLoadingReport(false);
    }
  };

  // Re-run parsing for existing samples using stored addressComponents only (no Google calls)
  const rerunParsingForSamples = async () => {
    try {
      setBackfilling(true);
      const docs = await getDocuments('address_samples');
      // Process sequentially; compute parserOutput from addressComponents
      for (const doc of docs) {
        const comps: any[] = Array.isArray(doc.addressComponents) ? doc.addressComponents : [];
        if (!comps.length) continue;

        const getFirst = (type: string): string | '' => {
          const c = comps.find((x) => Array.isArray(x.types) && x.types.includes(type));
          return (c?.long_name as string) || '';
        };

        const normalizeAscii = (s: string): string => {
          if (!s) return '';
          // Prefer ASCII by falling back to short_name if long_name contains non-ASCII
          const isAscii = (t: string) => !/[^\ -\u007F]/.test(t);
          if (isAscii(s)) return s;
          const c = comps.find((x) => x.long_name === s);
          return isAscii(c?.short_name || '') ? (c?.short_name as string) : s;
        };

  const building = normalizeAscii(getFirst('premise') || getFirst('street_number'));
        const route = normalizeAscii(getFirst('route'));
        const sl1 = normalizeAscii(getFirst('sublocality_level_1'));
        const sl2 = normalizeAscii(getFirst('sublocality_level_2'));
        const sl3 = normalizeAscii(getFirst('sublocality_level_3'));
        const neighborhood = normalizeAscii(getFirst('neighborhood'));
        const pincode = getFirst('postal_code');

        // Deterministic outputs
        const suggestedAreaName = sl1 || '';
        const suggestedLocalityName = (sl2 || sl3 || neighborhood || route || '');

        // Fail-fast semantics: skip update if area/locality missing
        if (!suggestedAreaName || !suggestedLocalityName) {
          continue;
        }

  // Route-only street name per required behavior
  const streetName = route || '';

        // Sub-locality (for display): prefer most specific available
        const subLocality = sl3 || sl2 || sl1 || '';

        const newParserOutput = {
          building: building || '',
          streetName: streetName || '',
          landmark: '',
          subLocality: subLocality || '',
          suggestedAreaName,
          suggestedLocalityName,
          pincode: pincode || '',
          confidence: 0.7,
        };

        await updateDocument('address_samples', doc.documentId || doc.id, {
          parserOutput: newParserOutput,
          updatedAt: new Date(),
        });
        // tiny delay to keep UI responsive
        await new Promise((r) => setTimeout(r, 50));
      }

      await loadSamples();
    } catch (e) {
      console.error('Backfill failed:', e);
    } finally {
      setBackfilling(false);
    }
  };

  const loadGoogleMaps = () => {
    return new Promise((resolve, reject) => {
      if ((window as any).google && (window as any).google.maps) {
        resolve(true);
        return;
      }
      
      if ((window as any).googleMapsLoading) {
        const checkInterval = setInterval(() => {
          if ((window as any).google && (window as any).google.maps) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
        return;
      }
      
      (window as any).googleMapsLoading = true;
      
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        reject(new Error('Google Maps API key not found'));
        return;
      }
      
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        (window as any).googleMapsLoading = false;
        resolve(true);
      };
      
      script.onerror = () => {
        (window as any).googleMapsLoading = false;
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });
  };

  const initializeMap = async (lat: number, lng: number) => {
    if (!mapRef.current) return;
    
    try {
      await loadGoogleMaps();
      
      const { google } = window as any;
      
      if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
        console.log('Google Maps not fully loaded, retrying...');
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!google || !google.maps || !google.maps.Map || !google.maps.Marker) {
          console.error('Google Maps API not fully loaded after retry');
          return;
        }
      }
      
      const mapOptions = {
        zoom: 12,
        center: { lat, lng },
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        mapTypeControl: true,
        fullscreenControl: true,
        gestureHandling: 'cooperative'
      };

      const map = new google.maps.Map(mapRef.current, mapOptions);
      mapInstanceRef.current = map;

      google.maps.event.addListenerOnce(map, 'idle', () => {
        // Add click listener to the map
        map.addListener('click', async (event: any) => {
          const clickedLocation = event.latLng;
          const lat = clickedLocation.lat();
          const lng = clickedLocation.lng();
          
          setSelectedLocation({ lat, lng });
          
          // Remove existing marker
          if (markerRef.current) {
            markerRef.current.setMap(null);
          }
          
          // Add new marker
          const marker = new google.maps.Marker({
            position: { lat, lng },
            map: map,
            title: 'Click to test address parsing',
            icon: {
              path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
              scale: 8,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }
          });
          
          markerRef.current = marker;
          
          // Automatically parse address when location is clicked
          await parseAddressForLocation(lat, lng);
        });

        setMapLoaded(true);
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to load Google Maps. Please check your API key configuration.');
    }
  };

  const parseAddressForLocation = async (lat: number, lng: number) => {
    setLoading(true);
    setError('');
    setAddressResult(null);
    setRawGoogleResponse(null);
    setDeterministicChoice(null);
    
    try {
  const result = await geocodingService.parseForOutletFields(lat, lng);
      
      if (result.success && result.addressFields) {
        setAddressResult(result.addressFields);
        setRawGoogleResponse(result.rawResponse);

        // Log a sample for analysis with placeIds for sublocalities
        try {
          await logAddressComponentsSample(
            'AddressValidator',
            lat,
            lng,
            result.rawResponse,
            result.addressFields
          );
        } catch (e) {
          // Non-blocking
          console.warn('Address sampling log failed:', e);
        }

        // Determine locality via deterministic path with placeId verification
        try {
          const choice = await determineDeterministicLocality(result.rawResponse, result.addressFields);
          setDeterministicChoice(choice);
          if (!choice) {
            alert('No valid locality found via SL2/SL3/Neighborhood/Route.');
          }
        } catch (e) {
          console.warn('Deterministic locality selection failed:', e);
        }
      } else {
        setError(result.error || 'Failed to parse address');
      }
    } catch (error) {
      console.error('Error parsing address:', error);
      setError('Error processing address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get first component by type from a geocoder result
  const getFirstByType = (res: any, type: string): string | null => {
    try {
      const comp = res?.address_components?.find((c: any) => Array.isArray(c.types) && c.types.includes(type));
      return comp?.long_name ?? null;
    } catch {
      return null;
    }
  };

  const determineDeterministicLocality = async (
    rawRes: any,
    parsed: any
  ): Promise<{ name: string; placeId: string; type: 'sublocality_level_2' | 'sublocality_level_3' | 'neighborhood' | 'route' } | null> => {
    if (!placeIdLookup.isAvailable()) return null;

    const city = getFirstByType(rawRes, 'locality') || 'Mumbai';
    const sl1 = getFirstByType(rawRes, 'sublocality_level_1') || parsed?.suggestedAreaName || null;
    const sl2 = getFirstByType(rawRes, 'sublocality_level_2');
    const sl3 = getFirstByType(rawRes, 'sublocality_level_3');
    const neighborhood = getFirstByType(rawRes, 'neighborhood');
    const route = getFirstByType(rawRes, 'route');

    // Order: SL2 -> SL3 -> neighborhood -> route
    if (sl2) {
      const id = await placeIdLookup.getLocalityPlaceId(sl2, sl1 || undefined, city || undefined);
      if (id) return { name: sl2, placeId: id, type: 'sublocality_level_2' };
    }
    if (sl3) {
      const id = await placeIdLookup.getLocalityPlaceId(sl3, sl1 || sl2 || undefined, city || undefined);
      if (id) return { name: sl3, placeId: id, type: 'sublocality_level_3' };
    }
    if (neighborhood) {
      const id = await placeIdLookup.getNeighborhoodPlaceId(neighborhood, sl1 || sl2 || undefined, city || undefined);
      if (id) return { name: neighborhood, placeId: id, type: 'neighborhood' };
    }
    if (route) {
      const id = await placeIdLookup.getRoutePlaceId(route, city || undefined);
      if (id) return { name: route, placeId: id, type: 'route' };
    }
    return null;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(16);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setError('Unable to get your location. Please ensure location services are enabled.');
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 rounded hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h1 className="text-xl font-semibold text-gray-900">Address Validator</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={getCurrentLocation}
              className="px-3 py-2 rounded-md text-blue-600 hover:bg-blue-50 border border-blue-200"
              title="Go to my location"
            >
              <span className="inline-flex items-center gap-2"><Navigation className="w-4 h-4" /> My Location</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex">
            <button
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'google' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}
              onClick={() => setActiveTab('google')}
            >
              Google Map
            </button>
            <button
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'osm' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}
              onClick={() => setActiveTab('osm')}
            >
              OSM Grid (Beta)
            </button>
            <button
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'reports' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}
              onClick={() => setActiveTab('reports')}
            >
              Reports
            </button>
          </div>
        </div>

        {/* Removed instructional banner per request */}

        {/* Main Workspace: Google Map */}
        {activeTab === 'google' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="relative">
              <div ref={mapRef} className="w-full" style={{ height: '560px', minHeight: '24rem' }} />
              {!mapLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}
              {loading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="bg-white rounded-lg p-4 flex items-center space-x-3">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-700">Parsing address...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* OSM Grid Tab (placeholder + controls) */}
        {activeTab === 'osm' && (
          <OsmGridPanel />
        )}

        {/* Error Display (Google tab only) */}
        {activeTab === 'google' && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-800">Error</h4>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Selected Location Info (Google tab only) */}
        {activeTab === 'google' && selectedLocation.lat !== 0 && selectedLocation.lng !== 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-800">Selected Location</h4>
                <p className="text-sm text-gray-600">
                  {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                </p>
              </div>
              <button
                onClick={() => copyToClipboard(`${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`)}
                className="p-2 text-gray-500 hover:text-gray-700"
                title="Copy coordinates"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Address Parsing Results (Google tab only) */}
        {activeTab === 'google' && addressResult && (
          <div className="space-y-4">
            {/* Deterministic locality selection result */}
            {deterministicChoice && (
              <div className="bg-white rounded-lg shadow-sm p-4 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-md font-semibold text-emerald-800">Deterministic Locality</h3>
                    <p className="text-sm text-emerald-700 mt-1">
                      Type: <span className="font-medium">{deterministicChoice.type}</span> ·
                      Name: <span className="font-medium">{deterministicChoice.name}</span>
                    </p>
                  </div>
                  <div className="text-xs text-gray-600 break-all">
                    PlaceId: <span className="font-mono">{deterministicChoice.placeId}</span>
                  </div>
                </div>
              </div>
            )}
            {/* Outlet Address Fields */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Outlet Address Fields</span>
                </h3>
                <div className="text-sm text-gray-500">
                  Confidence: {Math.round(addressResult.confidence * 100)}%
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Building</label>
                    <div className="p-2 bg-gray-50 rounded border text-sm">
                      {addressResult.building || <span className="text-gray-400 italic">Not found</span>}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Name</label>
                    <div className="p-2 bg-gray-50 rounded border text-sm">
                      {addressResult.streetName || <span className="text-gray-400 italic">Not found</span>}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                    <div className="p-2 bg-gray-50 rounded border text-sm">
                      {addressResult.suggestedAreaName || <span className="text-gray-400 italic">Not found</span>}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Locality</label>
                    <div className="p-2 bg-gray-50 rounded border text-sm">
                      {addressResult.suggestedLocalityName || <span className="text-gray-400 italic">Not found</span>}
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                    <div className="p-2 bg-gray-50 rounded border text-sm">
                      {addressResult.pincode || <span className="text-gray-400 italic">Not found</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Raw Google Response */}
            {rawGoogleResponse && (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Raw Google Maps Response</h3>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(rawGoogleResponse, null, 2))}
                    className="p-2 text-gray-500 hover:text-gray-700"
                    title="Copy raw response"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="bg-gray-900 rounded p-4 overflow-auto">
                  <pre className="text-sm text-green-400">
                    <code>{JSON.stringify(rawGoogleResponse, null, 2)}</code>
                  </pre>
                </div>
              </div>
            )}

            {/* Address Components Breakdown */}
            {rawGoogleResponse && rawGoogleResponse.address_components && (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Address Components Breakdown</h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium text-gray-700">Component</th>
                        <th className="text-left p-2 font-medium text-gray-700">Types</th>
                        <th className="text-left p-2 font-medium text-gray-700">Usage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rawGoogleResponse.address_components.map((component: any, index: number) => {
                        let usage = '';
                        const types = component.types;
                        
                        if (types.includes('premise')) usage = 'Building';
                        else if (types.includes('route')) usage = 'Street Name';
                        else if (types.includes('landmark')) usage = 'Street Name (landmark)';
                        else if (types.includes('sublocality_level_1')) usage = 'Area';
                        else if (types.includes('sublocality_level_3')) usage = 'Locality';
                        else if (types.includes('sublocality_level_2')) usage = 'Locality (fallback)';
                        else if (types.includes('postal_code')) usage = 'Pincode';
                        else usage = 'Not used';
                        
                        return (
                          <tr key={index} className="border-b">
                            <td className="p-2 font-mono text-blue-600">
                              {component.long_name}
                            </td>
                            <td className="p-2">
                              <div className="flex flex-wrap gap-1">
                                {component.types.map((type: string, typeIndex: number) => (
                                  <span 
                                    key={typeIndex}
                                    className="px-2 py-1 bg-gray-100 rounded text-xs"
                                  >
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="p-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                usage === 'Not used' 
                                  ? 'bg-gray-100 text-gray-600' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {usage}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Samples Report (Reports tab) */}
        {activeTab === 'reports' && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Samples Report</h3>
              <div className="flex gap-2">
                <button
                  onClick={loadSamples}
                  className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 border border-slate-300"
                >
                  <span className="inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</span>
                </button>
                <button
                  onClick={rerunParsingForSamples}
                  disabled={backfilling}
                  className="px-3 py-2 rounded-md text-purple-700 hover:bg-purple-50 border border-purple-300 disabled:opacity-50"
                  title="Re-run parsing (no Google API calls)"
                >
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> {backfilling ? 'Re-running…' : 'Re-run parsing'}
                  </span>
                </button>
              </div>
            </div>
            {loadingReport ? (
              <div className="text-sm text-gray-600">Loading samples...</div>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Created</th>
                      <th className="text-left p-2">Document ID</th>
                      <th className="text-left p-2">Lat</th>
                      <th className="text-left p-2">Lng</th>
                      <th className="text-left p-2">Raw SL1</th>
                      <th className="text-left p-2">Raw SL2</th>
                      <th className="text-left p-2">Raw SL3</th>
                      <th className="text-left p-2">Raw Neighborhood</th>
                      <th className="text-left p-2">Route</th>
                      <th className="text-left p-2">Parsed Area</th>
                      <th className="text-left p-2">Parsed Locality</th>
                      <th className="text-left p-2">PlaceId (SL1)</th>
                      <th className="text-left p-2">PlaceId (SL2)</th>
                      <th className="text-left p-2">PlaceId (SL3)</th>
                      <th className="text-left p-2">PlaceId (Neighborhood)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {samples.map((s: any, idx: number) => {
                      const ts = s.createdAt?.toDate?.() ?? (s.createdAt ? new Date(s.createdAt) : null);
                      const routeComp = Array.isArray(s.addressComponents) ? s.addressComponents.find((c: any) => Array.isArray(c.types) && c.types.includes('route')) : undefined;
                      const routeName = routeComp?.long_name || s.parserOutput?.streetName || '';
                      const rawSl1 = s.detected?.sublocality_level_1 ?? (Array.isArray(s.addressComponents) ? s.addressComponents.find((c: any) => Array.isArray(c.types) && c.types.includes('sublocality_level_1'))?.long_name : '') ?? '';
                      const rawSl2 = s.detected?.sublocality_level_2 ?? (Array.isArray(s.addressComponents) ? s.addressComponents.find((c: any) => Array.isArray(c.types) && c.types.includes('sublocality_level_2'))?.long_name : '') ?? '';
                      const rawSl3 = s.detected?.sublocality_level_3 ?? (Array.isArray(s.addressComponents) ? s.addressComponents.find((c: any) => Array.isArray(c.types) && c.types.includes('sublocality_level_3'))?.long_name : '') ?? '';
                      const rawNeighborhood = s.detected?.neighborhood ?? (Array.isArray(s.addressComponents) ? s.addressComponents.find((c: any) => Array.isArray(c.types) && c.types.includes('neighborhood'))?.long_name : '') ?? '';
                      const parsedArea = s.parserOutput?.suggestedAreaName ?? '';
                      const parsedLocality = s.parserOutput?.suggestedLocalityName ?? '';
                      return (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2 whitespace-nowrap">{ts ? ts.toLocaleString() : ''}</td>
                          <td className="p-2 whitespace-nowrap font-mono text-xs">{s.documentId || s.id || ''}</td>
                          <td className="p-2">{s.coordinates?.lat?.toFixed?.(6) ?? ''}</td>
                          <td className="p-2">{s.coordinates?.lng?.toFixed?.(6) ?? ''}</td>
                          <td className="p-2">{rawSl1}</td>
                          <td className="p-2">{rawSl2}</td>
                          <td className="p-2">{rawSl3}</td>
                          <td className="p-2">{rawNeighborhood}</td>
                          <td className="p-2">{routeName}</td>
                          <td className="p-2">{parsedArea}</td>
                          <td className="p-2">{parsedLocality}</td>
                          <td className="p-2 font-mono text-xs">{s.placeIds?.area_sublocality_level_1 || ''}</td>
                          <td className="p-2 font-mono text-xs">{s.placeIds?.locality_sublocality_level_2 || ''}</td>
                          <td className="p-2 font-mono text-xs">{s.placeIds?.locality_sublocality_level_3 || ''}</td>
                          <td className="p-2 font-mono text-xs">{s.placeIds?.locality_neighborhood || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressValidator;
